// A barra que aparece quando há mensagens selecionadas.
//
// Ela SUBSTITUI a barra normal em vez de somar a ela. Duas barras
// empilhadas roubariam altura da lista justamente quando a pessoa precisa
// ver o que selecionou — e deixariam dois lugares com botões, que é como
// alguém clica no errado.
//
// O QUE ESTA TELA PRECISA DEIXAR CLARO, em ordem de importância:
//   1. quantas estão selecionadas  (senão a ação em lote é uma aposta)
//   2. como sair da seleção         (senão a pessoa fica presa nela)
//   3. o que dá para fazer

import { useState } from "react";

import * as correio from "../api/correio";
import { explicar } from "../api/problema";
import { Icone } from "../ui/Icone";
import { Etiqueta } from "./Marcadores";
import { MenuDePastas } from "./Pastas";

export function BarraDeSelecao({
  pasta,
  selecionadas,
  carregadas,
  aoSelecionarTodas,
  aoLimpar,
  aoTerminar,
  sugestoes,
}: {
  pasta: string;
  selecionadas: Set<string>;
  /** Quantas já vieram do servidor. Ver o aviso de "selecionar todas". */
  carregadas: number;
  aoSelecionarTodas: () => void;
  aoLimpar: () => void;
  aoTerminar: () => void;
  /**
   * Marcadores JÁ em uso nas mensagens carregadas. Mesma fonte do menu de uma
   * mensagem só, em Leitura.tsx — e é o que impede a lista de virar
   * "Clientes", "clientes", "Cliente".
   */
  sugestoes: string[];
}) {
  const [ocupado, setOcupado] = useState("");
  const [erro, setErro] = useState("");
  const [marcador, setMarcador] = useState("");
  const [pondoMarcador, setPondoMarcador] = useState(false);
  // O que casa com o que já foi digitado. Sem diferenciar maiúscula, porque
  // quem digita "jh" procurando "Jhou" está certo e a tela é que erraria.
  const combinam = sugestoes
    .filter((m) => m.toLowerCase().includes(marcador.trim().toLowerCase()))
    .slice(0, 6);

  const uuids = [...selecionadas];
  const demais = uuids.length > correio.MAXIMO_POR_LOTE;

  async function executar(nome: string, acao: () => Promise<unknown>) {
    setOcupado(nome);
    setErro("");
    try {
      await acao();
      aoTerminar();
    } catch (e) {
      // FALHA NÃO LIMPA A SELEÇÃO. Se limpasse, a pessoa teria que escolher
      // tudo de novo para tentar outra vez — e com cinquenta mensagens isso
      // é o bastante para ela desistir e fazer uma a uma.
      setErro(explicar(e));
    } finally {
      setOcupado("");
    }
  }

  const naLixeira = pasta === "Trash";

  return (
    <div className="border-b border-linha-suave bg-acao-clara">
      <div className="flex flex-wrap items-center gap-1 px-2 py-2 text-tinta">
        <button
          onClick={aoLimpar}
          className="p-2 rounded-full hover:bg-white/60"
          title="Cancelar seleção"
          aria-label="Cancelar seleção"
        >
          <Icone nome="X" tamanho={18} />
        </button>

        <span className="px-1 text-sm font-medium tabular-nums">
          {uuids.length} selecionada{uuids.length > 1 ? "s" : ""}
        </span>

        {uuids.length < carregadas && (
          <button onClick={aoSelecionarTodas} className="px-2 text-xs text-acao underline">
            selecionar as {carregadas} carregadas
          </button>
        )}

        <span className="mx-1 h-5 w-px bg-linha" aria-hidden="true" />

        {/* NA LIXEIRA A AÇÃO É RESTAURAR, não apagar de vez.
            Apagar definitivamente em lote é irreversível, e um clique só —
            num botão que fica ao lado de outros cinco — é pouco para isso.
            Apagar de vez continua existindo por mensagem; em lote entra
            quando tiver confirmação digitada, como a remoção de caixa já
            tem. Enquanto não tem, não oferecer é melhor que oferecer mal. */}
        {naLixeira ? (
          <Acao
            icone="RotateCcw"
            rotulo="Restaurar para a Caixa de entrada"
            ocupado={ocupado === "restaurar"}
            desabilitado={demais || ocupado !== ""}
            aoClicar={() => void executar("restaurar", () => correio.mover(uuids, "INBOX"))}
          />
        ) : (
          <Acao
            icone="Trash2"
            rotulo="Mover para a lixeira"
            ocupado={ocupado === "lixeira"}
            desabilitado={demais || ocupado !== ""}
            aoClicar={() => void executar("lixeira", () => correio.mover(uuids, "Trash"))}
          />
        )}
        {pasta !== "Archive" && (
        <Acao
          icone="Archive"
          rotulo="Arquivar"
          ocupado={ocupado === "arquivar"}
          desabilitado={demais || ocupado !== ""}
          aoClicar={() => void executar("arquivar", () => correio.mover(uuids, "Archive"))}
        />
        )}
        {/* Dentro da própria Junk, "é spam" não faz nada — e botão que não
            faz nada é botão que ensina a desconfiar dos outros. */}
        {pasta !== "Junk" && (
        <Acao
          icone="ShieldAlert"
          rotulo="É spam"
          ocupado={ocupado === "spam"}
          desabilitado={demais || ocupado !== ""}
          aoClicar={() => void executar("spam", () => correio.mover(uuids, "Junk"))}
        />
        )}

        <span className="mx-1 h-5 w-px bg-linha" aria-hidden="true" />

        <Acao
          icone="MailOpen"
          rotulo="Marcar como lida"
          ocupado={ocupado === "lida"}
          desabilitado={demais || ocupado !== ""}
          aoClicar={() =>
            void executar("lida", () => correio.marcarVarias(uuids, { lida: true }))
          }
        />
        <Acao
          icone="Mail"
          rotulo="Marcar como não lida"
          ocupado={ocupado === "naolida"}
          desabilitado={demais || ocupado !== ""}
          aoClicar={() =>
            void executar("naolida", () => correio.marcarVarias(uuids, { lida: false }))
          }
        />
        <Acao
          icone="Star"
          rotulo="Marcar com estrela"
          ocupado={ocupado === "estrela"}
          desabilitado={demais || ocupado !== ""}
          aoClicar={() =>
            void executar("estrela", () => correio.marcarVarias(uuids, { marcada: true }))
          }
        />
        {/* MOVER PARA PASTA, que não existia.
            As ações eram lixeira, arquivar, spam, lida, não lida, estrela e
            marcador — nenhuma punha as selecionadas numa pasta do usuário.

            E É UM MENU PRÓPRIO, NÃO UM <select> NATIVO. A primeira versão era
            um `select`, e a Emilia (lamparima.com.br) relatou em 30/10/2026
            a lista saindo cortada e sobreposta. O `select` nativo é desenhado
            pelo SISTEMA, não pelo CSS da página: no Chrome do macOS ele vira
            menu do sistema operacional e parece certo, e em outros ele é
            desenhado pelo navegador e a estilização da página não o alcança.
            Estilizar `select` é apostar que todo sistema desenhe igual. */}
        <MenuDePastas
          atual={pasta}
          desabilitado={demais || ocupado !== ""}
          aoEscolher={(destino) =>
            void executar("mover", () => correio.mover(uuids, destino))
          }
        />
        <Acao
          icone="Tag"
          rotulo="Pôr um marcador"
          ocupado={false}
          desabilitado={demais || ocupado !== ""}
          aoClicar={() => setPondoMarcador((v) => !v)}
        />
      </div>

      {pondoMarcador && (
        <div className="px-3 pb-2">
        <form
          className="flex items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            const nome = marcador.trim();
            if (!nome) return;
            void executar("marcador", () =>
              // ACRESCENTAR, e não substituir: as mensagens selecionadas têm
              // marcadores diferentes entre si, e uma lista única apagaria o
              // que cada uma tinha de próprio.
              correio.marcarVarias(uuids, { acrescentar_marcadores: [nome] }),
            ).then(() => {
              setMarcador("");
              setPondoMarcador(false);
            });
          }}
        >
          <input
            value={marcador}
            onChange={(e) => setMarcador(e.target.value)}
            placeholder="nome do marcador"
            aria-label="Marcador a acrescentar"
            autoFocus
            className="min-w-0 flex-1 rounded-lg border border-linha px-3 py-1.5 text-sm"
          />
          <button
            type="submit"
            disabled={!marcador.trim() || ocupado !== ""}
            className="rounded-lg bg-acao px-3 py-1.5 text-sm text-white disabled:opacity-50"
          >
            {ocupado === "marcador" ? "aplicando…" : "aplicar"}
          </button>
        </form>

        {/* O QUE FALTAVA, e custou uma confusão inteira em 30/10/2026: o campo
            era texto livre, sem nenhuma lista. Quem digitava "jh" procurando
            um marcador existente não via nada — e não tinha como saber se era
            porque não existe ou porque a tela não mostra. */}
        {combinam.length > 0 && (
          <div className="mt-1 flex flex-wrap gap-1">
            {combinam.map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setMarcador(m)}
                className="rounded-full px-1 hover:bg-fundo"
              >
                <Etiqueta nome={m} />
              </button>
            ))}
          </div>
        )}

        {/* E O AVISO DE QUE VAI CRIAR UM NOVO. Sem ele, aplicar um nome que
            não existe parece "achou" — foi assim que uma PASTA chamada `jhou`
            quase virou um MARCADOR `jhou` diferente dela, com o mesmo nome. */}
        {marcador.trim() !== "" && combinam.length === 0 && (
          <p className="mt-1 text-xs text-tinta-media">
            Nenhum marcador com esse nome. Aplicar cria um novo — se você queria
            uma <strong>pasta</strong>, use "Mover para pasta".
          </p>
        )}
        </div>
      )}

      {/* O TETO É DO SERVIDOR, e a tela avisa ANTES em vez de deixar o
          botão falhar. Recusar depois do clique é pior: a pessoa já achava
          que tinha feito. */}
      {demais && (
        <p className="px-3 pb-2 text-xs text-perigo">
          São {uuids.length} mensagens, e o limite por vez é{" "}
          {correio.MAXIMO_POR_LOTE}. Selecione menos para continuar.
        </p>
      )}

      {erro && (
        <p className="px-3 pb-2 text-xs text-perigo" role="alert">
          {erro}
        </p>
      )}
    </div>
  );
}

function Acao({
  icone,
  rotulo,
  aoClicar,
  ocupado,
  desabilitado,
}: {
  icone: Parameters<typeof Icone>[0]["nome"];
  rotulo: string;
  aoClicar: () => void;
  ocupado: boolean;
  desabilitado: boolean;
}) {
  return (
    <button
      onClick={aoClicar}
      disabled={desabilitado}
      // O rótulo vai em `title` E em `aria-label`: ícone sozinho é adivinhação
      // para quem usa a tela pela primeira vez, e invisível para leitor de
      // tela.
      title={rotulo}
      aria-label={rotulo}
      className="p-2 rounded-full hover:bg-white/60 disabled:opacity-40"
    >
      <Icone nome={ocupado ? "RefreshCw" : icone} tamanho={18} />
    </button>
  );
}
